import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, DialogContent, IconButton, ListItemIcon, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { AccountBalanceWallet, AddCircleOutline, Delete, DeleteOutline, ReceiptOutlined, Refresh } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast } from 'react-toastify';
import TableView from '../../components/TableView';
import ConfirmDialog from '../../components/ConfirmDialog';
import ExportMenuButton from '../../components/ExportMenuButton';
import {
  MoleDialog,
  MoleDialogHeader,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { periodRequestConfig, useDateRange } from '../../hooks/useDateRange';
import { Util } from '../../util/Util';
import type { ClientWalletRow, WalletRecordRow } from './walletTypes';
import { isReceiptableWalletRecord, isTransferTag, walletSignedAmount } from './walletTypes';
import ReceiptDialog from '../../components/ReceiptDialog';
import type { ReceiptSource } from '../../util/receiptPdf';
import { walletRecordColumns } from './walletColumns';
import { useWalletRecordDelete } from './useWalletRecordDelete';
import { canPrintReceipt, canRecord } from '../../util/access';

type Props = {
  isOpen: boolean;
  wallet: ClientWalletRow | null;
  onClose: () => void;
  onPost?: () => void;
  onChanged?: () => void;
  refreshToken?: number;
};

const WalletClientModal = ({ isOpen, wallet, onClose, onPost, onChanged, refreshToken = 0 }: Props) => {
  const { t } = useUI();
  const { api, access } = useAuth();
  const themeColor = useMoleThemeColor();
  const { dateRange: period, setDateRange: setPeriod } = useDateRange();
  const [records, setRecords] = useState<WalletRecordRow[]>([]);
  const [detail, setDetail] = useState<ClientWalletRow | null>(wallet);
  const [loading, setLoading] = useState(false);
  const canPost = canRecord(access, 'wallet');
  const mayPrintReceipt = canPrintReceipt(access, 'read');
  const [receiptSource, setReceiptSource] = useState<ReceiptSource | null>(null);

  const {
    canDelete,
    canDeleteBulk,
    toDelete,
    toDeleteBulk,
    setToDelete,
    setToDeleteBulk,
    toastIdRef,
    handleDelete,
    handleBulkDelete,
    requestRowDelete,
    requestSelectionDelete,
    isConfirmOpen,
  } = useWalletRecordDelete(() => {
    void load();
    onChanged?.();
  });

  const load = async () => {
    if (!wallet) return;
    setLoading(true);
    const [walletRes, recordsRes] = await Promise.all([
      api.get(`/wallets/${wallet.client_id}`),
      api.get(`/wallets/${wallet.client_id}/records`, periodRequestConfig(period)),
    ]);
    if (walletRes?.status === 200) setDetail(walletRes.data);
    if (recordsRes?.status === 200) {
      setRecords(Array.isArray(recordsRes.data) ? recordsRes.data : recordsRes.data?.items || []);
      toast.update(toastIdRef.current, {
        render: t('common:toast.success.refresh'),
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen && wallet) {
      setDetail(wallet);
      void load();
    }
  }, [isOpen, wallet?.client_id, refreshToken, period.from?.toISOString(), period.to?.toISOString()]);

  const columns = useMemo(
    () => walletRecordColumns(t).filter((column) => column.accessorKey !== 'client_name'),
    [t],
  );

  return (
    <>
    <MoleDialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      themeColor={themeColor}
      paperOptions={{ maxWidth: '95%', width: '95%', maxHeight: '95vh' }}
    >
      <MoleDialogHeader
        title={t('common:wallet.clientRecordsTitle', { name: detail?.client || wallet?.client || '' })}
        subtitle={
          detail
            ? t('common:wallet.recordsTitle', {
                name: detail.client,
                balance: Util.formatNumber(detail.balance),
              })
            : undefined
        }
        icon={<AccountBalanceWallet />}
        onClose={onClose}
        themeColor={themeColor}
      />
      <DialogContent sx={{ pt: 1 }}>
        {detail && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('common:wallet.balance')}
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {Util.formatNumber(detail.balance)}
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">
                {t('common:ui.labels.status')}
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <Chip
                  size="small"
                  color={detail.is_active ? 'success' : 'default'}
                  label={detail.is_active ? t('common:wallet.active') : t('common:wallet.inactive')}
                />
              </Box>
            </Box>
          </Stack>
        )}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TableView
				posting="wallet"
            dataScope="wallet_record"
            header={t('common:wallet.movements')}
            data={records}
            columns={columns}
            showHubBack={false}
            period={period}
            setDateRangeFilter={setPeriod}
            isLoading={loading}
            totals={[{ label: t('common:tables.amount'), accessorFn: (row) => walletSignedAmount(row) }]}
            enableRowActions={canDelete}
            renderTopToolbarActions={(table) => (
              <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                <Tooltip title={t('common:ui.tooltips.refreshData')}>
                  <IconButton onClick={() => void load()} disabled={loading} color="primary">
                    <Refresh />
                  </IconButton>
                </Tooltip>
                {canPost && detail?.is_active && onPost && (
                  <Tooltip title={t('common:wallet.postMovement')}>
                    <IconButton onClick={onPost} color="success">
                      <AddCircleOutline />
                    </IconButton>
                  </Tooltip>
                )}
                {canDeleteBulk && (table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
                  <Tooltip title={t('common:ui.tooltips.deleteSelection')}>
                    <IconButton color="error" onClick={() => requestSelectionDelete(table)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
                <ExportMenuButton
                  prefix="wallet-transactions"
                  data={records}
                  columns={columns}
                  table={table}
                />
              </Box>
            )}
            renderRowActionMenuItems={(row, closeMenu) => [
              ...(mayPrintReceipt && isReceiptableWalletRecord(row.original) ? [
                <MenuItem
                  key="receipt"
                  onClick={() => {
                    setReceiptSource({ kind: 'wallet', id: Number(row.original.id) });
                    closeMenu();
                  }}
                >
                  <ListItemIcon><ReceiptOutlined fontSize="small" /></ListItemIcon>
                  {t('common:receipt.action')}
                </MenuItem>,
              ] : []),
              ...(canDelete ? [
                <MenuItem
                  key="delete"
                  onClick={() => {
                    requestRowDelete(row.original);
                    closeMenu();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon sx={{ color: 'error.main' }}><Delete fontSize="small" /></ListItemIcon>
                  {t('common:ui.buttons.delete')}
                </MenuItem>,
              ] : []),
            ]}
          />
        </LocalizationProvider>
      </DialogContent>
      <ReceiptDialog source={receiptSource} onClose={() => setReceiptSource(null)} />
    </MoleDialog>
      <ConfirmDialog
        onConfirm={toDelete ? handleDelete : handleBulkDelete}
        onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]); }}
        isOpen={isConfirmOpen}
        setIsOpen={() => {}}
        title={<><DeleteOutline sx={{ color: 'red' }} /> {t('common:dialog.deleteConfirmation')}</>}
        message={
          toDelete ? (
            <>
              {t('common:wallet.confirm.deleteSingleAmount')}&nbsp;
              <b><u>{Util.formatNumber(toDelete.amount)}</u></b>&nbsp;
              {t('common:wallet.confirm.deleteSingleDate')} <b><u>{Util.formatDate(toDelete.date, 'full')}</u></b>&nbsp;
              {t('common:wallet.confirm.deleteSingleClient')} <b><u>{toDelete.client_name}</u></b>!
              {isTransferTag(toDelete.tag) && (
                <>
                  <br />
                  {t('common:wallet.confirm.deleteTransferNote')}
                </>
              )}
            </>
          ) : (
            <>{t('common:wallet.confirm.deleteBulk', { count: Number(toDeleteBulk.length) })}</>
          )
        }
        confirmBtnColor="error"
        confirmBtnText={t('common:ui.buttons.delete')}
      />
    </>
  );
};

export default WalletClientModal;
